import { useEffect, useRef } from 'react'
import { Controller, useWatch, type UseFormSetError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormField, { FormGrid } from '../../../components/ui/FormField'
import InfoBanner from '../../../components/ui/InfoBanner'
import FormDateTimeField from '../../../components/ui/form/FormDateTimeField'
import FormTextareaField from '../../../components/ui/form/FormTextareaField'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useToast } from '../../../components/ui/ToastProvider'
import { getApiErrorCode, getApiErrorField, isOptimisticLock } from '../../../api/apiError'
import { GRANTS } from '../../../auth/grants'
import { useGrants } from '../../../auth/useCurrentUser'
import { parseISODate, toISODate, todayStart } from '../../../lib/datetime'
import { resolveFieldError } from '../../../lib/forms/messages'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { useSaveAgenda } from '../hooks/useAgendas'
import type { AgendaDetail, AgendaScope, AgendaUpsert } from '../types'
import AgendaGroupInviteField from './AgendaGroupInviteField'
import AgendaParticipantsField from './AgendaParticipantsField'
import AgendaRecurrenceFields from './AgendaRecurrenceFields'
import {
  AGENDA_DESCRIPTION_MAX,
  AGENDA_TITLE_MAX,
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  agendaFormSchema,
  shiftedEnd,
  splitLocalDateTime,
  toLocalDateTime,
  wallClockMs,
  type AgendaControl,
  type AgendaFormValues,
} from './agendaFormSchema'

interface Props {
  visible: boolean
  /** Düzenlenen toplantı; `null` → yeni kayıt. */
  initial: AgendaDetail | null
  /**
   * Seriye bağlı toplantıda kullanıcının seçtiği kapsam (126). `SERIES` → "Serinin tamamı" (gün kilitli,
   * tekrar bitişi düzenlenebilir); yoksa yalnız bu toplantı. Tek toplantıda yok sayılır.
   */
  scope?: AgendaScope
  /** Yeni kayıtta önerilen gün (`YYYY-MM-DD`); verilmezse bugün. Düzenlemede yok sayılır. */
  defaultDate?: string
  onHide: () => void
  onSaved: (saved: AgendaDetail) => void
  /** 409 (optimistic lock ya da toplantı bitti): çağıran formu kapatıp detayı yeniden okur. */
  onConflict?: () => void
}

/**
 * Formun kipi: yeni davet, tek toplantı düzenleme, serinin tek toplantısı ("Yalnız bu toplantı") ya da
 * "Serinin tamamı".
 */
type FormMode = 'create' | 'single' | 'occurrence' | 'series'

const PARTICIPANT_INPUT_ID = 'agenda-participants'
const GROUP_INPUT_ID = 'agenda-group-invite'

type ServerFieldRule = { field: keyof AgendaFormValues; message: string }

/**
 * Sunucu `field` → form alanı + mesaj. Sunucu tarih+saati tek değer doğruladığı için
 * `startTime`/`endTime` hataları saat alanlarına düşer. Önce tam ad, sonra kök ad aranır
 * (`recurrence.endDate` ile `recurrence` farklı hatalardır).
 */
const SERVER_FIELDS: Record<string, ServerFieldRule> = {
  title: { field: 'title', message: 'validation.invalid' },
  description: { field: 'description', message: 'validation.invalid' },
  startTime: { field: 'startTime', message: 'agenda.validation.startInPast' },
  endTime: { field: 'endTime', message: 'validation.timeRange' },
  participants: { field: 'participants', message: 'agenda.validation.participantsInvalid' },
  'recurrence.type': { field: 'recurrenceType', message: 'validation.invalid' },
  'recurrence.endDate': { field: 'recurrenceEndDate', message: 'validation.invalid' },
  recurrence: { field: 'recurrenceEndDate', message: 'agenda.validation.recurrenceTooLarge' },
}

/** `participants[0].employeeId` gibi iç içe alan adı → kök alan (`participants`). */
function rootField(field: string | undefined): string {
  return (field ?? '').split(/[.[]/)[0]
}

function serverRule(field: string | undefined): ServerFieldRule | undefined {
  return SERVER_FIELDS[field ?? ''] ?? SERVER_FIELDS[rootField(field)]
}

function modeOf(initial: AgendaDetail | null, scope: AgendaScope | undefined): FormMode {
  if (!initial) return 'create'
  if (!initial.series) return 'single'
  return scope === 'SERIES' ? 'series' : 'occurrence'
}

function toFormValues(initial: AgendaDetail | null, defaultDate: string, mode: FormMode): AgendaFormValues {
  if (!initial) {
    return {
      title: '',
      description: '',
      startDate: defaultDate,
      startTime: DEFAULT_START_TIME,
      endDate: defaultDate,
      endTime: DEFAULT_END_TIME,
      participants: [],
      recurring: false,
      recurrenceType: null,
      recurrenceEndDate: '',
      seriesEdit: false,
      originalRecurrenceEndDate: '',
      seriesMember: false,
    }
  }
  const start = splitLocalDateTime(initial.startTime)
  const end = splitLocalDateTime(initial.endTime)
  const series = mode === 'series' ? initial.series : undefined
  return {
    title: initial.title,
    description: initial.description ?? '',
    startDate: start.date,
    startTime: start.time,
    endDate: end.date,
    endTime: end.time,
    participants: initial.participants.map((p) => ({ ...p })),
    recurring: !!series,
    recurrenceType: series?.type ?? null,
    recurrenceEndDate: series?.endDate ?? '',
    seriesEdit: !!series,
    originalRecurrenceEndDate: series?.endDate ?? '',
    seriesMember: !!initial.series,
  }
}

function toPayload(v: AgendaFormValues, initial: AgendaDetail | null): AgendaUpsert {
  const body: AgendaUpsert = {
    title: v.title,
    description: v.description ? v.description : null,
    startTime: toLocalDateTime(v.startDate, v.startTime),
    endTime: toLocalDateTime(v.endDate, v.endTime),
    // Tam liste — eklenen/çıkarılanı (ve bildirimleri) sunucu hesaplar.
    participants: v.participants.map((p) => ({
      employeeId: p.employeeId,
      firstName: p.firstName ?? null,
      lastName: p.lastName ?? null,
      businessMail: p.businessMail ?? null,
      jobTitleTr: p.jobTitleTr ?? null,
      jobTitleEn: p.jobTitleEn ?? null,
      // Liste etiketi yalnız Grup Daveti'yle gelende var; yoksa alan hiç gönderilmez (125 gövdesi aynen).
      ...(p.managerListId == null ? {} : { managerListId: p.managerListId }),
    })),
  }
  // Tekrar yalnız işaretliyse gider: seri düzenlemesinde sıklık değişmediği için yalnız bitiş, o da yalnız
  // DEĞİŞTİYSE (126) — değişmeyen bitiş gönderilmez, sunucu serininkini korur.
  if (v.recurring && !v.seriesEdit) {
    body.recurrence = { type: v.recurrenceType, endDate: v.recurrenceEndDate }
  } else if (v.seriesEdit && v.recurrenceEndDate !== v.originalRecurrenceEndDate) {
    body.recurrence = { endDate: v.recurrenceEndDate }
  }
  return initial ? { ...body, modDate: initial.modDate } : body
}

/** Sunucu doğrulama hatasını alana yazar; eşlenemezse `false` (çağıran genel hata gösterir). */
function applyValidationError(err: unknown, setError: UseFormSetError<AgendaFormValues>): boolean {
  if (getApiErrorCode(err) !== 'VALIDATION_ERROR') return true
  const rule = serverRule(getApiErrorField(err))
  if (!rule) return false
  setError(rule.field, { type: 'server', message: rule.message })
  return true
}

const HEADER_KEYS: Record<FormMode, string> = {
  create: 'agenda.new',
  single: 'agenda.editTitle',
  occurrence: 'agenda.recurrence.editOccurrenceTitle',
  series: 'agenda.recurrence.editSeriesTitle',
}

/**
 * Yeni davet / düzenleme formu. Yeni davet seçilen günde 08:00–08:30 ile açılır; başlangıç kayınca
 * bitiş süreyi koruyarak kayar. Takvim bugünden (bitişte başlangıç gününden) öncesini seçtirmez.
 * Tekrarlı toplantı (126): yeni davette "Tekrarlı Toplantı"; seride kip `scope`'a göre.
 */
export default function AgendaFormDialog({
  visible,
  initial,
  scope,
  defaultDate,
  onHide,
  onSaved,
  onConflict,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const { create, update } = useSaveAgenda()
  const { control, handleSubmit, reset, setError, getValues, setValue, formState } = useZodForm(
    agendaFormSchema,
    { defaultValues: toFormValues(null, '', 'create') },
  )
  const submitting = create.isPending || update.isPending
  const startDate = useWatch({ control, name: 'startDate' })
  /** Son geçerli başlangıç (duvar saati ms) — bitişin kayma miktarı buna göre ölçülür. */
  const lastStart = useRef<number | null>(null)
  const wasVisible = useRef(false)
  /** Açılış anındaki kayıt — form açıkken detay arka planda tazelense de düzenleme bunu esas alır. */
  const opened = useRef<AgendaDetail | null>(initial)
  /** Açılış anındaki kip — kayıt bunu esas alır (`opened` ile aynı gerekçe). */
  const openedMode = useRef<FormMode>(modeOf(initial, scope))
  const mode = modeOf(initial, scope)

  // Form YALNIZ açılışta doldurulur. `initial` her arka plan tazelemesinde (pencere odağı, bir
  // katılımcının yanıtı) yeni referansla gelir; her seferinde reset etmek kullanıcının yazdıklarını
  // sessizce silerdi.
  useEffect(() => {
    const opening = visible && !wasVisible.current
    wasVisible.current = visible
    if (!opening) return
    opened.current = initial
    openedMode.current = modeOf(initial, scope)
    const values = toFormValues(initial, defaultDate ?? toISODate(todayStart()), openedMode.current)
    reset(values)
    // Açılış kaydırma sayılmaz: sayaç yeni değerle kurulur.
    lastStart.current = wallClockMs(values.startDate, values.startTime)
  }, [visible, initial, scope, defaultDate, reset])

  /**
   * Başlangıç değişince bitiş aynı süreyi koruyarak kayar. Yazım sırasındaki geçersiz ara değerler
   * ("1", "10:") atlanır; değer geçerli olunca son geçerli başlangıca göre kayar.
   */
  const onStartChange = () => {
    const v = getValues()
    const next = wallClockMs(v.startDate, v.startTime)
    if (next === null) return
    const shifted = shiftedEnd(lastStart.current, next, v)
    lastStart.current = next
    if (!shifted) return
    // Gönderimden sonra bitişin hatası anında güncellensin; öncesinde form sessiz kalır.
    const opts = { shouldValidate: formState.isSubmitted }
    setValue('endDate', shifted.date, opts)
    setValue('endTime', shifted.time, opts)
  }

  const onError = (err: unknown) => {
    if (isOptimisticLock(err)) {
      onConflict?.()
      return
    }
    if (getApiErrorCode(err) === 'INVALID_STATE') {
      toast.error(t('agenda.errors.ended'))
      onConflict?.()
      return
    }
    if (!applyValidationError(err, setError)) toast.error(t('common.errors.generic'))
  }

  const onValid = (v: AgendaFormValues) => {
    const base = opened.current
    const body = toPayload(v, base)
    const done = { onSuccess: onSaved, onError }
    if (!base) {
      create.mutate(body, done)
      return
    }
    const saveScope = openedMode.current === 'series' ? 'SERIES' : undefined
    update.mutate({ id: base.id, body, scope: saveScope }, done)
  }

  const today = todayStart()
  const startDay = parseISODate(startDate)
  const datesLocked = mode === 'series'

  return (
    <FormDialog
      visible={visible}
      header={t(HEADER_KEYS[mode])}
      submitLabel={t('agenda.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
      width="40rem"
    >
      <ScopeNote mode={mode} t={t} />
      <FormGrid>
        <FormTextField
          control={control}
          name="title"
          label={t('agenda.form.title')}
          required
          full
          maxLength={AGENDA_TITLE_MAX}
        />
        <FormTextareaField
          control={control}
          name="description"
          label={t('agenda.form.description')}
          maxLength={AGENDA_DESCRIPTION_MAX}
          rows={4}
        />
        <FormDateTimeField
          control={control}
          dateName="startDate"
          timeName="startTime"
          label={t('agenda.form.start')}
          dateLabel={t('agenda.form.startDate')}
          timeLabel={t('agenda.form.start')}
          required
          minDate={today}
          onValueChange={onStartChange}
          dateDisabled={datesLocked}
        />
        <FormDateTimeField
          control={control}
          dateName="endDate"
          timeName="endTime"
          label={t('agenda.form.end')}
          dateLabel={t('agenda.form.endDate')}
          timeLabel={t('agenda.form.end')}
          required
          minDate={startDay && startDay > today ? startDay : today}
          dateDisabled={datesLocked}
        />
        <ParticipantsField control={control} t={t} />
        {(mode === 'create' || mode === 'series') && (
          <AgendaRecurrenceFields
            control={control}
            mode={mode}
            seriesType={opened.current?.series?.type}
          />
        )}
      </FormGrid>
    </FormDialog>
  )
}

/** Seriye bağlı toplantıda değişikliğin kapsamını söyler (126). */
function ScopeNote({ mode, t }: Readonly<{ mode: FormMode; t: TFunction }>) {
  if (mode === 'series') {
    return <InfoBanner icon="pi pi-sync" title={t('agenda.recurrence.seriesNote')} />
  }
  if (mode === 'occurrence') {
    return <InfoBanner icon="pi pi-info-circle" title={t('agenda.recurrence.occurrenceNote')} />
  }
  return null
}

/**
 * Katılımcı seçici + altında grup daveti — ikisi de `Controller` ile aynı `participants` değerine
 * bağlı; hata metni paylaşılan `FormField` kutusundadır.
 *
 * <p>Grup daveti yalnız `MANAGER_LIST_MANAGE` sahibine çizilir: listeleri okuyan uç bu yetkiyi ister,
 * Ajanda ise tüm rollere açıktır — yetkisiz kullanıcıda seçici 403 alıp boş kalırdı.
 */
function ParticipantsField({ control, t }: Readonly<{ control: AgendaControl; t: TFunction }>) {
  const canInviteGroup = useGrants().hasGrant(GRANTS.MANAGER_LIST_MANAGE)
  return (
    <Controller
      control={control}
      name="participants"
      render={({ field, fieldState }) => (
        <>
          <FormField
            label={t('agenda.form.attendees')}
            htmlFor={PARTICIPANT_INPUT_ID}
            required
            full
            error={resolveFieldError(fieldState.error?.message, t)}
          >
            <AgendaParticipantsField
              inputId={PARTICIPANT_INPUT_ID}
              value={field.value}
              onChange={field.onChange}
              placeholder={t('agenda.form.attendeesHint')}
              invalid={!!fieldState.error}
              describedBy={`${PARTICIPANT_INPUT_ID}-err`}
            />
          </FormField>
          {canInviteGroup && (
            <FormField label={t('agenda.form.groupInvite')} htmlFor={GROUP_INPUT_ID} full>
              <AgendaGroupInviteField
                inputId={GROUP_INPUT_ID}
                value={field.value}
                onChange={field.onChange}
              />
            </FormField>
          )}
        </>
      )}
    />
  )
}
